// AI Governance tile on the main /dashboard.
// Light design system (Colors.lightTheme + Fonts.js tokens, mirroring
// theme.css): white surfaces, hairline borders, shared AddOnCard KPI tiles,
// and semicircle gauges per framework (same atom Compliance Ready uses).
// No gradient blocks, no rainbow accent stripes, no decorative emojis.

import React, { useEffect, useState } from 'react';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';

import { ai_dashboard, ai_compliance_evaluate } from '../services';
import { unwrap } from '../services/HelperMethod/aigEnvelope';
import CommonButton from '../Components/CommonButton';
import AddOnCard from '../Dashboard/AddOns/AddOnCard';
import { lightTheme as T } from '../Constants/Colors';
import { fontFamily, fontSize, fontWeight } from '../Constants/Fonts';

// Portal opens in its own tab so /dashboard stays the home base.
// noopener+noreferrer prevents reverse-tabnabbing from the portal side.
const PORTAL_URL = '/ai-adoption-portal';
const openPortal = () => window.open(PORTAL_URL, '_blank', 'noopener,noreferrer');

// Gauge palette — pulled from the light-theme status tokens so the widget
// reads as one of the family (Risk Registry / Compliance Ready / Add-Ons).
const TRAIL = T.border;
const RING = {
    pass: T.success,
    warn: T.warning,
    fail: T.danger,
    inert: T.textMuted,
};
function ringColor(pct) {
    if (pct == null) return RING.inert;
    if (pct >= 80) return RING.pass;
    if (pct >= 50) return RING.warn;
    return RING.fail;
}

export default function AIAdoptionWidget() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [evaluating, setEvaluating] = useState(false);

    const fetchDashboard = () =>
        ai_dashboard()
            .then(r => setData(unwrap(r) || null))
            .catch(() => { });

    useEffect(() => {
        let cancelled = false;
        ai_dashboard()
            .then(r => { if (!cancelled) { setData(unwrap(r) || null); setLoading(false); } })
            .catch(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    const runEvaluation = async () => {
        if (evaluating) return;
        setEvaluating(true);
        try {
            await ai_compliance_evaluate();
            await fetchDashboard();
        } catch (e) { /* surfaced separately */ }
        setEvaluating(false);
    };

    const totalAssets = data?.total_assets || 0;
    const openViolations = data?.open_violations || 0;
    const eventsToday = data?.events_today || 0;
    const highRiskAssets = (data?.risk_distribution?.high || 0)
        + (data?.risk_distribution?.unacceptable || 0);

    const frameworks = data?.framework_readiness || [];

    // KPI tile — reuses the shared AddOnCard: label (+ muted sub caption) on
    // the left, the big value as the trailing action. `valueColor` lets a tile
    // flag risk (danger) without changing the rest of the chrome.
    const Kpi = ({ value, label, sub, valueColor }) => (
        <AddOnCard
            label={
                <span style={{ display: 'block' }}>
                    <span style={{
                        display: 'block', fontFamily, fontSize: fontSize.sm, fontWeight: fontWeight.semibold,
                        textTransform: 'uppercase', letterSpacing: '0.06em', color: T.textSecondary,
                    }}>{label}</span>
                    {sub && (
                        <span style={{ display: 'block', fontFamily, fontSize: fontSize.xs, color: T.textMuted, marginTop: 3 }}>
                            {sub}
                        </span>
                    )}
                </span>
            }
            action={
                <span style={{
                    fontFamily, fontSize: fontSize['5xl'], fontWeight: fontWeight.bold,
                    lineHeight: 1.1, letterSpacing: '-0.01em', color: valueColor || T.textPrimary,
                }}>
                    {loading ? '—' : value}
                </span>
            }
        />
    );

    // Framework gauge — semicircle, matches the GRC card pattern used by
    // Compliance Ready. Same circleRatio, same trail colour, same place
    // for the percentage. Severity colour applies to the ring, not the
    // surrounding chrome — so the row reads at a glance without visual noise.
    const Gauge = ({ fw }) => {
        const pct = fw.evaluated ? Math.round(fw.readiness_pct || 0) : null;
        const path = ringColor(pct);
        return (
            <div
                onClick={() => openPortal()}
                style={{
                    background: T.surfaceMuted,
                    border: `1px solid ${T.border}`,
                    borderRadius: T.radiusSm,
                    padding: '14px 14px 12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    minHeight: 92,
                }}>
                <div style={{ width: 78, height: 50, position: 'relative', flexShrink: 0 }}>
                    <CircularProgressbar
                        value={pct == null ? 0 : pct}
                        circleRatio={0.5}
                        styles={buildStyles({
                            rotation: 0.75,
                            pathColor: path,
                            trailColor: TRAIL,
                            strokeLinecap: 'round',
                        })}
                    />
                    <div style={{
                        position: 'absolute', inset: 0,
                        display: 'flex', alignItems: 'flex-end', justifyContent: 'center', paddingBottom: 2,
                    }}>
                        <span style={{ fontFamily, fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: T.textPrimary }}>
                            {pct == null ? '—' : `${pct}%`}
                        </span>
                    </div>
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontFamily, fontSize: fontSize.base, fontWeight: fontWeight.semibold, color: T.textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {fw.label}
                    </div>
                    <div style={{ fontFamily, fontSize: fontSize.sm, color: T.textMuted, marginTop: 3 }}>
                        {fw.evaluated
                            ? `${fw.controls_met} of ${fw.controls_total} controls met`
                            : 'Not evaluated'}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div>
            {/* Header — same shape Risk Registry uses */}
            <div className='d-flex align-items-center justify-content-between mb-3'>
                <div className='d-flex gap-3 align-items-center'>
                    <div style={{
                        width: 32, height: 32, borderRadius: T.radiusSm,
                        background: T.primarySoft,
                        border: `1px solid ${T.border}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: T.primary, fontFamily, fontSize: fontSize.base, fontWeight: fontWeight.semibold, letterSpacing: '0.04em',
                    }}>AI</div>
                    <h3 style={{ margin: 0, fontFamily, fontSize: fontSize['3xl'], fontWeight: fontWeight.semibold, color: T.textPrimary }}>AI Adoption</h3>
                </div>
                <div className='d-flex gap-2 align-items-center'>
                    <CommonButton text={evaluating ? 'Evaluating…' : 'Re-evaluate frameworks'} disable={evaluating || loading} onClick={runEvaluation} />

                    <CommonButton  text="Open AI portal" onClick={() => openPortal()} />

                </div>
            </div>

            <div className="addons_container">
                {/* KPI strip — four uniform tiles */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
                    <Kpi value={totalAssets} label="AI assets" sub="Discovered" />
                    <Kpi value={highRiskAssets} label="High-risk" sub="EU AI Act high + unacceptable" valueColor={highRiskAssets > 0 ? T.danger : undefined} />
                    <Kpi value={openViolations} label="Open violations" sub="Across active policies" valueColor={openViolations > 0 ? T.danger : undefined} />
                    <Kpi value={eventsToday || '—'} label="Events (24h)" sub="Prompt + DLP capture" />
                </div>

                {/* Framework readiness — semicircle gauges, one per framework.
                Caps at 6 for layout density; the portal shows the full set. */}
                <div style={{
                    background: T.surface,
                    border: `1px solid ${T.border}`,
                    borderRadius: T.radius,
                    padding: 16,
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
                        <h4 style={{ margin: 0, fontFamily, fontSize: fontSize.lg, fontWeight: fontWeight.semibold, color: T.textPrimary }}>Framework readiness</h4>
                        <span style={{ fontFamily, fontSize: fontSize.sm, color: T.textMuted }}>
                            {frameworks.filter(f => f.evaluated).length} of {frameworks.length} evaluated
                        </span>
                    </div>
                    {loading ? (
                        <div style={{ padding: 24, textAlign: 'center', fontFamily, color: T.textMuted, fontSize: fontSize.base }}>Loading…</div>
                    ) : frameworks.length === 0 ? (
                        <div style={{ padding: 24, textAlign: 'center', fontFamily, color: T.textMuted, fontSize: fontSize.base }}>
                            No frameworks configured.
                        </div>
                    ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                            {frameworks.slice(0, 6).map(fw => (
                                <Gauge key={fw.code} fw={fw} />
                            ))}
                        </div>
                    )}
                    {frameworks.length > 6 && (
                        <div style={{ marginTop: 10, textAlign: 'center' }}>
                            <button
                                onClick={() => openPortal()}
                                style={{
                                    background: 'transparent',
                                    color: T.primary,
                                    border: 'none',
                                    fontFamily, fontSize: fontSize.base, fontWeight: fontWeight.medium,
                                    cursor: 'pointer',
                                    padding: 4,
                                }}>
                                View all {frameworks.length} frameworks in the portal →
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
